import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { BaseRespone } from '../models/common.response';
import * as jwt_decode from 'jwt-decode';

/**
 * Thrown by signIn() when the server reports the user already has an active web session.
 * Carries the metadata the sign-in component needs to render the "kick the other device?"
 * dialog. Caught by signIn callers; everything else can let it bubble.
 */
export class SessionTakeoverRequired extends Error {
    /**
     * String discriminator — checked instead of `instanceof` because Angular's lazy
     * module loading can produce two distinct class objects in different chunks,
     * which silently breaks `instanceof` across the boundary.
     */
    readonly kind = 'SessionTakeoverRequired' as const;

    constructor(public activeSession: ActiveSessionInfo, public credentials: { userName: string; password: string }) {
        super('A session is already active on another device.');
        // Restore the prototype so other parts of the codebase that DO use instanceof still work.
        Object.setPrototypeOf(this, SessionTakeoverRequired.prototype);
    }
}

/** Robust type-guard — survives bundle boundaries where `instanceof` does not. */
export function isSessionTakeoverRequired(value: any): value is SessionTakeoverRequired {
    return !!value && value.kind === 'SessionTakeoverRequired';
}

export interface ActiveSessionInfo {
    ipAddress?: string;
    userAgent?: string;
    createdOn: string;
    lastSeenOn: string;
    deviceType: string;
}

@Injectable()
export class AuthService {
    /** Emits whenever the access token is replaced so other services can react. */
    accessTokenChanged$ = new Subject<string>();

    private _authenticated = false;

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
    ) { }

    // ---------------------------------------------------------------------
    // Token accessors
    // ---------------------------------------------------------------------

    set accessToken(token: string) {
        if (token) {
            localStorage.setItem('accessToken', token);
        } else {
            localStorage.removeItem('accessToken');
        }
        this.accessTokenChanged$.next(token || '');
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    /**
     * UNIX seconds at which the access token expires. Returns 0 for missing / malformed tokens.
     * Read every time — we never cache because the token is rotated by silent refresh.
     */
    get accessTokenExpiresAt(): number {
        const token = this.accessToken;
        if (!token) { return 0; }
        try {
            const decoded: any = jwt_decode.jwtDecode(token);
            return typeof decoded?.exp === 'number' ? decoded.exp : 0;
        } catch {
            return 0;
        }
    }

    /** Seconds until access token expiry; negative if already expired. */
    secondsUntilExpiry(): number {
        const exp = this.accessTokenExpiresAt;
        if (!exp) { return -1; }
        return exp - Math.floor(Date.now() / 1000);
    }

    isAccessTokenExpired(skewSeconds = 0): boolean {
        const remaining = this.secondsUntilExpiry();
        return remaining <= skewSeconds;
    }

    // ---------------------------------------------------------------------
    // Password reset
    // ---------------------------------------------------------------------

    forgotPassword(credentials: { email: string }): Observable<any> {
        return this._httpClient
            .post(`${environment.baseUrl}/Auth/forgot-password`, credentials)
            .pipe(switchMap((response: any) => of(response)));
    }

    resetPassword(credentials: { password: string; email: string; token: string }): Observable<any> {
        return this._httpClient
            .post(`${environment.baseUrl}/Auth/reset-password`, credentials)
            .pipe(switchMap((response: any) => of(response)));
    }

    // ---------------------------------------------------------------------
    // Sign-in (with single-session enforcement)
    // ---------------------------------------------------------------------

    /**
     * Standard sign-in. If the server reports an existing active session and force=false,
     * the observable errors with a {@link SessionTakeoverRequired}. The caller catches it
     * to show the conflict dialog, then calls {@link signInForce} with the same credentials.
     */
    signIn(credentials: { userName: string; password: string }): Observable<any> {
        if (this._authenticated) {
            // RxJS 6 signature: throwError(error). The throwError(() => error) factory
            // form is RxJS 7+; on v6 the factory function itself becomes the emitted error.
            return throwError(new Error('User is already logged in.'));
        }
        return this._postLogin(credentials, false);
    }

    /** Re-runs login with force=true to evict the prior session. */
    signInForce(credentials: { userName: string; password: string }): Observable<any> {
        return this._postLogin(credentials, true);
    }

    private _postLogin(credentials: { userName: string; password: string }, force: boolean): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.baseUrl}/Auth/login`,
                { ...credentials, force, deviceType: 'web' },
                { withCredentials: true },
            )
            .pipe(
                switchMap((response: any) => {
                    const result = response?.result;

                    // Conflict branch — surface as a typed error so the UI can react.
                    if (result?.requiresSessionTakeover) {
                        return throwError(new SessionTakeoverRequired(result.activeSession, credentials));
                    }
                    // OTP branch — no token yet, just propagate so the component can navigate.
                    if (result?.requiresOtpVerification) {
                        return of(response);
                    }
                    // Authenticator MFA branch — no token yet, propagate so the component can
                    // navigate to the TOTP entry page. Email/SMS OTP is NOT sent in this case.
                    if (result?.requiresTotpVerification) {
                        return of(response);
                    }

                    this._storeLoginSuccess(result);
                    return of(response);
                }),
            );
    }

    // ---------------------------------------------------------------------
    // Authenticator MFA (TOTP) login
    // ---------------------------------------------------------------------

    /**
     * Second factor for an MFA-enabled login. `isRecoveryCode` switches the server to
     * recovery-code verification. Surfaces the same session-takeover flow as the other paths.
     */
    validateTotp(
        credentials: { transactionId: string; code: string; isRecoveryCode?: boolean },
        force = false,
    ): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.baseUrl}/Auth/mfa/totp/validate`,
                { ...credentials, isRecoveryCode: !!credentials.isRecoveryCode, force, deviceType: 'web' },
                { withCredentials: true },
            )
            .pipe(
                switchMap((response: any) => {
                    const result = response?.result;
                    if (result?.requiresSessionTakeover) {
                        return throwError(new SessionTakeoverRequired(result.activeSession, {
                            userName: credentials.transactionId,
                            password: credentials.code,
                        }));
                    }
                    this._storeLoginSuccess(result);
                    return of(response);
                }),
            );
    }

    // ---------------------------------------------------------------------
    // OTP
    // ---------------------------------------------------------------------

    validateOtp(credentials: { transactionId: string; otp: string }, force = false): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.baseUrl}/Auth/otp/validate`,
                { ...credentials, force, deviceType: 'web' },
                { withCredentials: true },
            )
            .pipe(
                switchMap((response: any) => {
                    const result = response?.result;
                    if (result?.requiresOtpVerification) {
                        return of(response);
                    }
                    if (result?.requiresSessionTakeover) {
                        return throwError(new SessionTakeoverRequired(result.activeSession, {
                            // OTP retry needs the transactionId, not username/password.
                            // Stash on the error object so the dialog can reissue.
                            userName: credentials.transactionId,
                            password: credentials.otp,
                        }));
                    }
                    this._storeLoginSuccess(result);
                    return of(response);
                }),
            );
    }

    // ---------------------------------------------------------------------
    // Emergency MFA recovery via email OTP (lost authenticator AND recovery codes)
    // ---------------------------------------------------------------------

    /**
     * Requests a one-time code by email for an MFA-enabled login when the user has lost both
     * their authenticator and their recovery codes. Pass the MFA login challenge transactionId.
     * Returns the OTP transactionId to echo back to {@link validateMfaEmailOtp}.
     */
    requestMfaEmailOtp(mfaTransactionId: string): Observable<any> {
        return this._httpClient
            .post<any>(`${environment.baseUrl}/Auth/mfa/email-otp/request`, { transactionId: mfaTransactionId })
            .pipe(switchMap((response: any) => of(response)));
    }

    /**
     * Validates the emergency email OTP. On success the SERVER disables authenticator MFA and
     * issues the session — the user is logged in and normal OTP login is restored afterwards.
     */
    validateMfaEmailOtp(credentials: { transactionId: string; otp: string }, force = false): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.baseUrl}/Auth/mfa/email-otp/validate`,
                { ...credentials, force, deviceType: 'web' },
                { withCredentials: true },
            )
            .pipe(
                switchMap((response: any) => {
                    const result = response?.result;
                    if (result?.requiresSessionTakeover) {
                        return throwError(new SessionTakeoverRequired(result.activeSession, {
                            userName: credentials.transactionId,
                            password: credentials.otp,
                        }));
                    }
                    this._storeLoginSuccess(result);
                    return of(response);
                }),
            );
    }

    resendOtp(transactionId: string): Observable<any> {
        return this._httpClient
            .post(`${environment.baseUrl}/Auth/otp/resend`, { transactionId })
            .pipe(switchMap((response: any) => of(response)));
    }

    // ---------------------------------------------------------------------
    // Refresh (silent)
    // ---------------------------------------------------------------------

    private _refreshInFlight$: Observable<boolean> | null = null;

    /**
     * Exchanges the httpOnly refresh-token cookie + the current (possibly expired) bearer
     * for a new access token. Coalesces concurrent callers into a single network request.
     */
    refreshAccessToken(): Observable<boolean> {
        if (this._refreshInFlight$) {
            return this._refreshInFlight$;
        }

        const subject = new Subject<boolean>();
        this._refreshInFlight$ = subject.asObservable();

        this._httpClient
            .post<any>(`${environment.baseUrl}/Auth/refresh`, {}, { withCredentials: true })
            .pipe(
                tap((response: any) => {
                    const result = response?.result;
                    if (result?.token) {
                        this._storeLoginSuccess(result);
                    } else {
                        throw new Error('Refresh response missing token');
                    }
                }),
                map(() => true),
                catchError((err: any) => {
                    // Only treat a genuine auth rejection as "session is gone". Network
                    // blips, CORS, and 5xx are transient — keep the existing token so a
                    // still-valid session (e.g. the proactive refresh that runs 60s
                    // before expiry) isn't wiped by a momentary hiccup, which would
                    // otherwise log an actively-working user out.
                    const status = err?.status;
                    if (status === 401 || status === 403) {
                        this._clearLocalSession();
                    }
                    return of(false);
                }),
            )
            .subscribe({
                next: (ok) => {
                    subject.next(ok);
                    subject.complete();
                    this._refreshInFlight$ = null;
                },
                error: () => {
                    subject.next(false);
                    subject.complete();
                    this._refreshInFlight$ = null;
                },
            });

        return this._refreshInFlight$;
    }

    // ---------------------------------------------------------------------
    // Sign-out
    // ---------------------------------------------------------------------

    /**
     * Asks the server to revoke this session, then clears local state.
     * Always resolves to true — server failures don't block the user from leaving.
     */
    signOut(): Observable<boolean> {
        const finalise = () => {
            this._clearLocalSession();
            return of(true);
        };

        if (!this.accessToken) {
            return finalise();
        }

        return this._httpClient
            .post(`${environment.baseUrl}/Auth/logout`, {}, { withCredentials: true })
            .pipe(
                catchError(() => of(null)),
                switchMap(finalise),
            );
    }

    // ---------------------------------------------------------------------
    // Sign-up / unlock — left as-is (still hit non-existent endpoints; rewire if revived)
    // ---------------------------------------------------------------------

    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    // ---------------------------------------------------------------------
    // Status checks
    // ---------------------------------------------------------------------

    /**
     * Used by the route guard. True when we have an unexpired token, or can refresh successfully.
     */
    check(): Observable<boolean> {
        if (!this.accessToken) {
            return of(false);
        }
        if (!this.isAccessTokenExpired(/* skew */ 5)) {
            this._authenticated = true;
            return of(true);
        }
        // Expired (or about to) — try silent refresh via the httpOnly cookie.
        return this.refreshAccessToken();
    }

    generateTokenFromCode(data: any): Observable<BaseRespone<any>> {
        return this._httpClient.post<BaseRespone<any>>(`${environment.baseUrl}/company/configuration`, data);
    }

    getRolesFromToken() {
        const token = this.accessToken;
        if (!token) { return null; }
        try {
            const decoded: any = jwt_decode.jwtDecode(token);
            return decoded?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
                ?? decoded?.role
                ?? null;
        } catch {
            return null;
        }
    }

    // ---------------------------------------------------------------------
    // Internal
    // ---------------------------------------------------------------------

    private _storeLoginSuccess(result: any): void {
        this.accessToken = result.token;
        this._authenticated = true;
        if (result.user) {
            this._userService.user = result.user;
        }
    }

    private _clearLocalSession(): void {
        localStorage.removeItem('accessToken');
        this._authenticated = false;
        this.accessTokenChanged$.next('');
    }
}
